import { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Heading,
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    Text,
    HStack,
    Button,
} from '@library';
import type { Bicycle, RenditionUrl } from '../types';

import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import useCustomNavigate from '../../core/hooks/router/useCustomNavigate';

import useCurrentStore from '../../core/hooks/useCurrentStore';
import MetaProductDetailsSidePane from './SparePartsSearch/MetaProductDetailsSidePane';
import styles from './BicycleSearchTable.module.scss';
import ProductImage from './ProductImage/ProductImage';
import DownloadSparePartPdfButton from './SparePartsSearch/DownloadSparePartPdfButton';
import SparePartsSkuList from './SparePartsSearch/SparePartsSearchResults/SparePartsSkuList';

export const TableHeadCellTitle = memo(
    ({ messageId }: { messageId: string }) => (
        <Heading h6 medium uppercase>
            <span>{messageId && <FormattedMessage id={messageId} />}</span>
        </Heading>
    )
);

const ProductSearchTableRow = memo(
    ({
        bicycle,
        onClick,
        isPdfSparePartSearch,
        onShowProperties,
    }: {
        bicycle: Bicycle;
        onClick?: () => void;
        isPdfSparePartSearch: boolean;
        onShowProperties(): void;
    }) => {
        const specificationAttributes = bicycle.specificationAttributes ?? [];
        return (
            <TableRow hoverEffect onPress={onClick} role="link">
                {!isPdfSparePartSearch && (
                    <TableCell>
                        <ProductImage
                            size="100px"
                            url={
                                bicycle.images?.[0]?.rendition176pxUrl.replace(
                                    '176px',
                                    '*'
                                ) as RenditionUrl
                            }
                            overviewPlacement="right"
                        />
                    </TableCell>
                )}
                <TableCell data-test-class="vehicleTableManufacturer">
                    {bicycle.manufacturerName}
                </TableCell>
                <TableCell data-test-class="vehicleTableModel">
                    {bicycle.model}
                </TableCell>
                {!isPdfSparePartSearch && (
                    <TableCell data-test-class="vehicleTableSkuList">
                        <div className={styles.sparePartsSkuList}>
                            <SparePartsSkuList
                                skuList={bicycle.skuList}
                                inline
                            />
                        </div>
                    </TableCell>
                )}
                <TableCell data-test-class="vehicleTableType" role="link">
                    {bicycle.type}
                </TableCell>
                <TableCell data-test-class="vehicleTableModelYear" role="link">
                    {bicycle.manufacturingEndYear ===
                    bicycle.manufacturingStartYear ? (
                        bicycle.manufacturingStartYear
                    ) : (
                        <span>
                            {bicycle.manufacturingStartYear} -{' '}
                            {bicycle.manufacturingEndYear || ''}
                        </span>
                    )}
                </TableCell>
                {!isPdfSparePartSearch && (
                    <TableCell
                        data-test-class="vehicleTableCategory"
                        role="link"
                    >
                        {bicycle.category}
                    </TableCell>
                )}
                {!isPdfSparePartSearch && (
                    <TableCell>
                        <HStack wrap gap="micro" align="center">
                            {specificationAttributes
                                .map(item => `${item.name}: ${item.value}`)
                                .slice(0, 2)
                                .map(item => (
                                    <Text key={item} secondary regular>
                                        {item}
                                    </Text>
                                ))}
                            {specificationAttributes.length > 2 && (
                                <Button
                                    text
                                    inline
                                    onClick={e => {
                                        e.stopPropagation();
                                        onShowProperties();
                                    }}
                                >
                                    <Text secondary>
                                        <FormattedMessage
                                            id="+{count} more"
                                            values={{
                                                count:
                                                    specificationAttributes.length -
                                                    2,
                                            }}
                                        />
                                    </Text>
                                </Button>
                            )}
                        </HStack>
                    </TableCell>
                )}
                {isPdfSparePartSearch && (
                    <TableCell>
                        <DownloadSparePartPdfButton bicycle={bicycle} />
                    </TableCell>
                )}
            </TableRow>
        );
    }
);

const TableHeadRow = memo(({ columns }: { columns: string[] }) => {
    return (
        <TableRow>
            {columns.map(heading => (
                <TableHeaderCell key={heading}>
                    <TableHeadCellTitle messageId={heading} />
                </TableHeaderCell>
            ))}
        </TableRow>
    );
});

const BicycleSearchTable = ({ bicycles }: { bicycles: Bicycle[] }) => {
    const navigate = useCustomNavigate();
    const [detailsPaneItemId, setDetailsPaneItemId] = useState<
        string | undefined
    >();
    const { companyCode } = useCurrentStore();

    const isPdfSparePartSearch = companyCode === 'hma';
    const { storeRootPath } = useStoreAndLanguageParams();

    const gotoBicycleSearch = (bicycle: Bicycle) => {
        if (isPdfSparePartSearch) {
            navigate({
                pathname: `${storeRootPath}/spare-parts-search-view`,
                search: `?sparePartParentId=${bicycle.id}`,
            });
        } else {
            navigate({
                pathname: `${storeRootPath}/spare-parts-search`,
                search: `?sparePartParentId=${bicycle.id}`,
            });
        }
    };

    const activeBicycle = bicycles.find(item => item.id === detailsPaneItemId);

    const headerColumns = isPdfSparePartSearch
        ? ['make', 'model', 'product type', 'modelYear', '']
        : [
              'image',
              'make',
              'model',
              'article number',
              'product type',
              'modelYear',
              'category',
              'Attributes',
          ];

    return (
        <div className={styles.bicycleSearchTable}>
            <Table stripedReversed>
                <TableHeader sticky dark>
                    <TableHeadRow columns={headerColumns} />
                </TableHeader>
                <TableBody>
                    {Array.from(bicycles).map(bicycle => (
                        <ProductSearchTableRow
                            isPdfSparePartSearch={isPdfSparePartSearch}
                            key={bicycle.id}
                            bicycle={bicycle}
                            onClick={() => gotoBicycleSearch(bicycle)}
                            onShowProperties={() => {
                                setDetailsPaneItemId(bicycle.id);
                            }}
                        />
                    ))}
                </TableBody>
            </Table>
            {activeBicycle && (
                <MetaProductDetailsSidePane
                    product={activeBicycle}
                    onClose={() => setDetailsPaneItemId(undefined)}
                />
            )}
        </div>
    );
};

export default memo(BicycleSearchTable);
